import React from "react";

/**
 * Slim contextual top strip for the authenticated portals.
 * Sits above the main content area: ~52 px tall, cream background,
 * hairline bottom border. Left: optional breadcrumb + page title.
 * Right: page-level actions.
 *
 * Usage inside a portal page:
 *   <ContextStrip
 *     breadcrumb={[{ to: "/admin", label: "Admin" }, { label: "Users" }]}
 *     title="User management"
 *     actions={<button>Export CSV</button>}
 *   />
 */
export default function ContextStrip({ breadcrumb, title, eyebrow, subtitle, actions }) {
  const hasCrumb = Array.isArray(breadcrumb) && breadcrumb.length > 0;
  return (
    <div
      data-testid="context-strip"
      className="flex items-center justify-between"
      style={{
        minHeight: 52,
        padding: "10px 0",
        borderBottom: "1px solid #D9D8E4",
        marginBottom: 24,
        gap: 16,
      }}
    >
      <div style={{ minWidth: 0 }}>
        {hasCrumb && (
          <nav data-testid="context-breadcrumb" style={{ fontSize: 12.5, lineHeight: 1.4 }}>
            {breadcrumb.map((c, i) => {
              const last = i === breadcrumb.length - 1;
              return (
                <React.Fragment key={i}>
                  {i > 0 && (
                    <span aria-hidden style={{ color: "#9AA0C0", margin: "0 6px" }}>›</span>
                  )}
                  {c.to && !last ? (
                    <a href={c.to} style={{ color: "#6C6B85", textDecoration: "none" }}>{c.label}</a>
                  ) : (
                    <span style={{ color: last ? "#1B1F4B" : "#6C6B85", fontWeight: last ? 600 : 400 }}>
                      {c.label}
                    </span>
                  )}
                </React.Fragment>
              );
            })}
          </nav>
        )}
        {eyebrow && !hasCrumb && (
          <div className="wn-label" style={{ color: "#8F3C23" }}>{eyebrow}</div>
        )}
        {title && (
          <h1
            data-testid="context-title"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 22,
              color: "#1B1F4B",
              lineHeight: 1.2,
              marginTop: hasCrumb ? 2 : 0,
            }}
          >
            {title}
          </h1>
        )}
        {subtitle && (
          <div
            data-testid="context-subtitle"
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 13,
              lineHeight: 1.5,
              marginTop: 4,
              maxWidth: 680,
            }}
          >
            {subtitle}
          </div>
        )}
      </div>
      {actions && (
        <div data-testid="context-actions" className="flex items-center gap-2 flex-shrink-0">
          {actions}
        </div>
      )}
    </div>
  );
}
